import {
    CalendarMonthOutlined,
    ChecklistOutlined,
    EmailOutlined,
    LinkedIn,
    PhoneOutlined,
} from "@mui/icons-material";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { canonicalPages } from "../canonical";
import { DEFAULT_BISON_SECONDARY_CONTACT } from "../canonical/default-contacts";
import { useVisiblePages } from "../hooks/useVisiblePages";
import { parseRoomPath, roomPath } from "../room-path";
import type { Room } from "../schema";
import type { WidgetOfType } from "../schema/widgets";
import { typeScale } from "../theme/tokens";
import {
    type Status,
    currentPhaseIndex,
    resolvePhases,
    stepMarkdownComponents,
} from "../widgets/MAP";
import { WidgetRenderer } from "../widgets/registry";
import { PageTitle } from "./PageTitle";
import type { PageTemplateProps } from "./types";

// Welcome page template. The nav handles brand chrome, so the page opens
// with the H1 + intro across the full width, then a two-column body whose
// columns start on the same line:
//
//   H1                                          (full width)
//   ┌─────────────────────────────────────┬──────────────────────┐
//   │  Intro (760px measure)         <----│---> 1. next step from │  tops align
//   │  "What this deal room is for"       │      the Checklist   │
//   │  Main column: page widgets in order │   2. Bison contact   │
//   │  (marketplace_grid, customer_logos) │   3. onboarding      │
//   │                                     │   4. content_card    │
//   │                                     │      widgets (CTAs)  │
//   └─────────────────────────────────────┴──────────────────────┘
//
// LAYOUT RULE (2026-09-08, Bill; third revision): the top of the first
// right-rail card sits level with the bottom of the H1, so the next step and
// both contact cards show above the fold. Only the H1 sits above the
// two-column Stack; the intro is the left column's first block at the 760px
// section measure. (2026-09-03 had the rail level with the first section's
// header, with the intro full width above both columns.) No magic top-margin
// on the rail, so the alignment holds at every desktop width.
//
// Two sections are rendered by the template, not stored (2026-09-08, Bill):
//   - "What this deal room is for" opens the main column: three short
//     paragraphs on what the room is, how both sides use it through the
//     partnership, and what still goes over email, then "How it works", the
//     three facts a first-time reader asks about before anything else (FAQ
//     wording, two paragraphs), styled as a second section of the same kind
//     (Bill). Every body paragraph on the page is body1 at line-height 1.65 in
//     text.secondary, the brand wall's description included. Plain
//     language, no links: the left nav already lists the pages. A page-by-page
//     link list stood here for a few hours the same day; a Hush Crib CEO / VP
//     Sales persona review called it the nav written out twice and Bill cut it.
//   - "Next step" opens the rail, above the contact cards: the first open step
//     of the current Checklist phase (same resolution as widgets/MAP.tsx), its
//     copy, and a button that deep-links to that row (#task-<id>). It replaces
//     the hand-written `content_card#next-step`, which named a person and went
//     stale the moment the step was done.
// Retired from Welcome the same day: the stored `rich_text#intro-message` and
// the canonical testimonials (two furniture quotes on a nursery brand's page;
// the brand wall carries the proof). Neither section names an individual or a
// date: any teammate of the supplier can sign in and read the page as written
// to them.
//
// Convention: in Welcome, content_card widgets are pulled into the right
// rail as CTAs. The next step and then the Bison contact cards sit ABOVE those
// CTAs in the same rail. Every other widget type stays in the main flow in
// the order it appears in the page JSON. Other templates (BasePage) render
// every widget in a single stack — only Welcome partitions.
export function Welcome({ page, room }: PageTemplateProps) {
    const intro =
        (page.data?.intro as string | undefined) ??
        "A quick look at what we've built together so far, and where we'd like to take this next.";

    const railWidgets = page.widgets.filter((w) => w.type === "content_card");
    // A pitch room (2026-09-24, Tvilum): the room's own widgets carry the
    // proposal and render before the canonical marketplace strip and logo
    // wall; the deal-room purpose block and its "Bison is the seller of
    // record" copy do not apply and are skipped.
    const isPitch = room.roomType === "pitch";
    const canonicalIds = new Set((canonicalPages.welcome?.widgets ?? []).map((w) => w.id));
    const mainWidgets = page.widgets
        .filter((w) => w.type !== "content_card")
        .sort((a, b) =>
            isPitch ? Number(canonicalIds.has(a.id)) - Number(canonicalIds.has(b.id)) : 0,
        );
    // Secondary (onboarding) contact: per-room override, else the canonical
    // default (Elijah Chang). Always present, so the rail always renders.
    const secondaryContact = room.bisonSecondaryContact ?? DEFAULT_BISON_SECONDARY_CONTACT;
    const hasRail = !!room.bisonContact || !!secondaryContact || railWidgets.length > 0;

    // The gated room and the staff preview differ only in the path prefix;
    // in-room links are built on whichever one the reader is on.
    const { pathname } = useLocation();
    const basePath = roomPath(room.slug, parseRoomPath(pathname)?.preview ?? false, room.roomType);

    return (
        <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 4, md: 6 }, maxWidth: 1280 }}>
            {/* The H1 sits above both columns, so the rail starts level with
                its bottom, but wraps at the left column's width (the page less
                the 340px rail and the 32px gap), balanced over its lines, so it
                never runs past the body text (2026-10-26). */}
            <Typography
                variant="h1"
                sx={{
                    ...typeScale.pageTitle,
                    mt: 0,
                    mb: 2,
                    textWrap: "balance",
                    maxWidth: hasRail ? { md: "calc(100% - 372px)" } : undefined,
                }}
            >
                <PageTitle title={page.title} highlight={page.data?.titleHighlight as string | undefined} />
            </Typography>
            {/* Stacked under md the columns must stretch: flex-start in a column
                stack shrink-wraps the main column to its widest child, so a
                markdown table pushed every paragraph past the phone screen
                (Bill, 2026-09-28). */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={4}
                alignItems={{ xs: "stretch", md: "flex-start" }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* The intro lives in the left column at the section
                        measure, so the rail starts level with the bottom of the
                        H1 and both contact cards show above the fold (Bill,
                        2026-09-08). */}
                    <Typography
                        variant="body1"
                        sx={{
                            color: "text.secondary",
                            maxWidth: 760,
                            fontSize: "1.125rem",
                            lineHeight: 1.65,
                            mb: 5,
                        }}
                    >
                        {intro}
                    </Typography>
                    <Stack spacing={5}>
                        {!isPitch && <WhatThisRoomIsFor />}
                        {mainWidgets.map((widget) => (
                            <WidgetRenderer key={widget.id} widget={widget} room={room} />
                        ))}
                    </Stack>
                </Box>

                {hasRail && (
                    <Box
                        sx={{
                            width: { xs: "100%", md: 340 },
                            flexShrink: 0,
                            position: { md: "sticky" },
                            // Pin offset only. It must stay BELOW the rail's
                            // natural top (the bottom of the H1) or sticky
                            // clamps the card downward at rest and breaks the
                            // alignment above. There is no fixed page header to
                            // clear; the nav is a full-height fixed sidebar.
                            top: { md: 24 },
                        }}
                    >
                        <Stack spacing={2}>
                            <ChecklistNextStep room={room} basePath={basePath} />
                            {room.bisonContact && (
                                <RailContactCard contact={room.bisonContact} label={room.bisonContact.cardLabel} />
                            )}
                            {secondaryContact && (
                                <RailContactCard
                                    contact={secondaryContact}
                                    label={
                                        secondaryContact.cardLabel ??
                                        (isPitch ? "Your Bison contact" : "Your onboarding contact")
                                    }
                                />
                            )}
                            {railWidgets.map((widget) => (
                                <WidgetRenderer key={widget.id} widget={widget} room={room} />
                            ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}

// ─── What this deal room is for ──────────────────────────────────────

// Canonical copy. No supplier name, no person, no date. The three facts are
// the FAQ's own answers (faq-canonical.ts) cut to one sentence each; change
// them there first, then here.
const ROOM_PURPOSE = [
    "This room is yours, and it stays open for as long as we work together. It is where the work of the partnership lives instead of getting lost in email threads: what we found on your listings, how a partnership with Bison runs, the agreements, and the checklist that takes us to your first orders.",
    "The checklist is the plan. Both sides tick steps as they are done and we keep it current as we go, so come back whenever you want to know where something stands. You read and sign the agreements here. Your product data, images and insurance certificate go on the Documents page, and every step and file has a link you can send to a colleague.",
    "Pricing, terms and anything you want in writing still go over email. This room is the record of where we landed. Anyone on your team can have their own sign-in link. Ask your Bison contact.",
];

const HOW_IT_WORKS = [
    "Your Amazon account stays yours. Nobody at Bison logs into it. We sell from our own account on the listings you already have, and Bison is the seller of record: we take the order, ship it, answer the customer and handle returns.",
    "Most partners are live within a month of signing, with paperwork, data and marketplace authorizations running in parallel.",
];

const OVERLINE_SX = {
    color: "text.secondary",
    letterSpacing: 1.5,
    fontWeight: 700,
    fontSize: "0.7rem",
} as const;

function WhatThisRoomIsFor() {
    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 2 }}>
                What this deal room is for
            </Typography>
            <Stack spacing={1.5} sx={{ maxWidth: 760 }}>
                {ROOM_PURPOSE.map((text) => (
                    <Typography
                        key={text}
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.65 }}
                    >
                        {text}
                    </Typography>
                ))}
            </Stack>
            <Typography variant="h4" sx={{ mt: 5, mb: 2 }}>
                How it works
            </Typography>
            <Stack spacing={1.5} sx={{ maxWidth: 760 }}>
                {HOW_IT_WORKS.map((text) => (
                    <Typography
                        key={text}
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.65 }}
                    >
                        {text}
                    </Typography>
                ))}
            </Stack>
        </Box>
    );
}

// ─── Next step, read from the Checklist ──────────────────────────────

function ChecklistNextStep({ room, basePath }: { room: Room; basePath: string }) {
    const { pages } = useVisiblePages(room);
    const mapPage = pages.find((p) => p.template === "map");
    const mapWidget = mapPage?.widgets.find((w): w is WidgetOfType<"map"> => w.type === "map");
    const mapWidgetId = mapWidget?.id;
    const [taskStatus, setTaskStatus] = useState<Record<string, Status> | null>(null);

    useEffect(() => {
        if (!mapWidgetId) return;
        let cancelled = false;
        fetch(`/api/rooms/${room.slug}/state`, { cache: "no-store" })
            .then((r) => r.json() as Promise<{ taskStatus?: Record<string, Status> }>)
            .then((s) => {
                if (!cancelled) setTaskStatus(s.taskStatus ?? {});
            })
            .catch(() => {
                if (!cancelled) setTaskStatus({});
            });
        return () => {
            cancelled = true;
        };
    }, [room.slug, mapWidgetId]);

    if (!mapPage || !mapWidget || taskStatus === null) return null;

    const phases = resolvePhases(mapWidget.props.phases, mapWidget.id, taskStatus);
    const idx = currentPhaseIndex(phases);
    const phase = idx >= 0 ? phases[idx] : null;
    const next = phase?.tasks.find((t) => !t.done) ?? null;
    const checklistHref = `${basePath}/${mapPage.slug}`;

    return (
        <Box
            sx={{
                p: 2.5,
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "background.paper",
            }}
        >
            <Typography variant="overline" sx={OVERLINE_SX}>
                Next step
            </Typography>
            {next && phase ? (
                <>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.3, mt: 1 }}>
                        {next.task.title}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.25 }}
                    >
                        {phase.name}
                    </Typography>
                    {next.task.description && (
                        <Box sx={{ mt: 1.25 }}>
                            <ReactMarkdown components={stepMarkdownComponents}>
                                {next.task.description}
                            </ReactMarkdown>
                        </Box>
                    )}
                    <Button
                        component={RouterLink}
                        to={`${checklistHref}#${next.anchor}`}
                        variant="contained"
                        color="secondary"
                        fullWidth
                        startIcon={<ChecklistOutlined />}
                        sx={{ mt: 1.75, textTransform: "none", fontWeight: 600 }}
                    >
                        Open in the checklist
                    </Button>
                </>
            ) : (
                <>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.3, mt: 1 }}>
                        Onboarding complete
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        Every step on the checklist is done.
                    </Typography>
                    <Button
                        component={RouterLink}
                        to={checklistHref}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<ChecklistOutlined />}
                        sx={{ mt: 1.75, textTransform: "none", fontWeight: 600 }}
                    >
                        Open the checklist
                    </Button>
                </>
            )}
        </Box>
    );
}

function RailContactCard({
    contact,
    label = "Your Bison contact",
}: {
    contact: NonNullable<PageTemplateProps["room"]["bisonContact"]>;
    label?: string;
}) {
    // Caption under the buttons: phone only. The email address used to print
    // here too; removed 2026-09-04 (CEO): the Email button already carries it
    // and the bare address cluttered the card.
    const captionParts: string[] = [];
    if (contact.phone) captionParts.push(contact.phone);

    return (
        <Box
            sx={{
                p: 2.5,
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "background.paper",
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: "text.secondary",
                    letterSpacing: 1.5,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                }}
            >
                {label}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.25, mb: 1.75 }}>
                <Avatar
                    src={contact.photoUrl}
                    alt={contact.name}
                    sx={{
                        width: 56,
                        height: 56,
                        bgcolor: "primary.main",
                        fontSize: "1.4rem",
                        fontWeight: 600,
                        flexShrink: 0,
                    }}
                >
                    {contact.name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: "1rem",
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color: "text.primary",
                        }}
                    >
                        {contact.name}
                    </Typography>
                    {contact.role && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.8rem", mt: 0.25, lineHeight: 1.3 }}
                        >
                            {contact.role}
                        </Typography>
                    )}
                </Box>
            </Stack>
            <Stack spacing={1}>
                {contact.calendlyUrl && (
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        href={contact.calendlyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<CalendarMonthOutlined />}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        Book a time
                    </Button>
                )}
                <Stack direction="row" spacing={1}>
                    {contact.email && (
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            href={`mailto:${contact.email}`}
                            startIcon={<EmailOutlined />}
                            size="small"
                            sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                            Email
                        </Button>
                    )}
                    {contact.phone && (
                        <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`}
                            startIcon={<PhoneOutlined />}
                            size="small"
                            sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                            Call
                        </Button>
                    )}
                </Stack>
                {contact.linkedinUrl && (
                    <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        href={contact.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<LinkedIn />}
                        size="small"
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        LinkedIn
                    </Button>
                )}
            </Stack>
            {captionParts.length > 0 && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        mt: 1.5,
                        fontSize: "0.75rem",
                        wordBreak: "break-word",
                    }}
                >
                    {captionParts.join(" · ")}
                </Typography>
            )}
        </Box>
    );
}

